// Generated by GitHub Copilot
import Row from "react-bootstrap/Row";
import Container from "react-bootstrap/Container";
import Col from "react-bootstrap/Col";
import { Alert, Spinner, Tab, Tabs } from "react-bootstrap";
import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import DeliveryStreamsList from "./DeliveryStreamsList";
import LeadershipSearch from "./LeadershipSearch";
import BrandManager from "./BrandManager";
import GlobalTogglesList from "./GlobalTogglesList";
import { trackConfigurationView, trackConfigurationTabSwitch } from '../../utils/modernUITracking';

import styles from "./Configuration.module.css";

function Configuration({ user }) {
  // Check user privileges for configuration access (must be before state initialization)
  const hasConfigurationPrivilege =
    user && user.privileges && user.privileges.configuration;
  // US1945335: Deity-only Global Toggles tab (runtime kill switches).
  const hasDeityPrivilege =
    user && user.privileges && user.privileges.deity;
  // US2028928: Modern UI soft-launch admin — surfaces the launch toggle inside
  // the Global Toggles tab (see GlobalTogglesList).
  const isModernUILaunchAdmin = user && user.modern_ui_launch_admin;
  // The Global Toggles tab is visible to deity users (kill switches) OR launch
  // admins (Modern UI soft-launch toggle).
  const canViewGlobalToggles = hasDeityPrivilege || isModernUILaunchAdmin;
  const canViewConfiguration =
    user &&
    user.privileges &&
    (user.privileges.configuration ||
      user.privileges.editor ||
      user.privileges.deity);

  // Generated by GitHub Copilot
  // Use URL query parameters for tab persistence instead of localStorage
  const [searchParams, setSearchParams] = useSearchParams();

  // Generated by GitHub Copilot
  const [loading, setLoading] = useState(true);
  // eslint-disable-next-line no-unused-vars
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(() => {
    const tabFromURL = searchParams.get('tab');
    const validTabs = ['delivery-streams', 'leadership-search', 'brands', 'global-toggles'];

    // Validate tab from URL exists and user has access
    if (tabFromURL && validTabs.includes(tabFromURL)) {
      // Check permissions for restricted tabs
      if (tabFromURL === 'brands' && !hasConfigurationPrivilege) {
        return 'delivery-streams';
      }
      if (tabFromURL === 'global-toggles' && !canViewGlobalToggles) {
        return 'delivery-streams';
      }
      return tabFromURL;
    }

    // Default to first tab
    return 'delivery-streams';
  });
  const tabsContainerRef = useRef(null);


  // Generated by GitHub Copilot
  // Update URL when tab changes and track tab switches
  useEffect(() => {
    // Get previous tab from URL before updating
    const previousTab = searchParams.get('tab');

    setSearchParams({ tab: activeTab }, { replace: true });

    // Track tab switch if tabs are different
    if (previousTab && previousTab !== activeTab) {
      trackConfigurationTabSwitch(previousTab, activeTab);
    }
  }, [activeTab, setSearchParams, searchParams]);

  // Generated by GitHub Copilot
  // Simulate loading for the page framework and track page view
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);

      // Track configuration page view
      trackConfigurationView({
        activeTab,
        hasWriteAccess: hasConfigurationPrivilege
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [activeTab, hasConfigurationPrivilege]);

  // Generated by GitHub Copilot
  // Loading state
  if (loading) {
    return (
      <Container className={styles.configurationContainer}>
        <Row>
          <Col xs={12}>
            <h2 id="configuration">Configuration</h2>
            <div className="text-center py-5">
              <Spinner animation="border" role="status" variant="primary">
                <span className="visually-hidden">Loading...</span>
              </Spinner>
              <p className="mt-2 text-muted">Loading configuration...</p>
            </div>
          </Col>
        </Row>
      </Container>
    );
  }

  // Generated by GitHub Copilot
  // Error state
  if (error) {
    return (
      <Container className={styles.configurationContainer}>
        <Row>
          <Col xs={12}>
            <h2 id="configuration">Configuration</h2>
            <Alert variant="danger" className="mt-3">
              <Alert.Heading>Configuration Error</Alert.Heading>
              <p>{error}</p>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  // Generated by GitHub Copilot
  // If user has no configuration viewing privileges, show access denied
  if (!canViewConfiguration) {
    return (
      <Container className={styles.configurationContainer}>
        <Row>
          <Col xs={12}>
            <h2 id="configuration">Configuration</h2>
            <Alert variant="warning" className="mt-4">
              <Alert.Heading>Access Restricted</Alert.Heading>
              <p>
                You need configuration, editor, or administrator privileges to
                access configuration settings.
              </p>
              <hr />
              <p className="mb-0">
                <small>
                  Contact your administrator if you need access to this feature.
                </small>
              </p>
            </Alert>
          </Col>
        </Row>
      </Container>
    );
  }

  return (
    <Container className={styles.configurationContainer}>
      <Row>
        <Col xs={12}>
          <h2 id="configuration">
            Configuration
            {/* Generated by GitHub Copilot */}
            {canViewConfiguration && !hasConfigurationPrivilege && (
              <span className="badge bg-secondary ms-3 fs-6">Read Only</span>
            )}
          </h2>
          <p className={styles.configurationDescription}>
            {hasConfigurationPrivilege
              ? "Manage system configuration settings and options. Select a configuration section below to view and modify settings."
              : canViewConfiguration
                ? "View system configuration settings and options. Editing requires configuration privilege."
                : "Access to configuration settings is restricted."}
          </p>

          {/* Generated by GitHub Copilot */}
          {/* Mobile: Dropdown navigation (< 768px) */}
          <div className={styles.mobileNav}>
            <select
              className={styles.mobileTabSelect}
              value={activeTab}
              onChange={(e) => setActiveTab(e.target.value)}
              aria-label="Select configuration section"
            >
              <option value="delivery-streams">Delivery Streams</option>
              <option value="leadership-search">Leadership Search</option>
              {hasConfigurationPrivilege && (
                <option value="brands">Brand Awareness</option>
              )}
              {/* US1945335 / US2028928: Global Toggles tab — deity (kill switches)
                  or Modern UI launch admins (soft-launch toggle). */}
              {canViewGlobalToggles && (
                <option value="global-toggles">Global Toggles</option>
              )}
            </select>
          </div>

          {/* Generated by GitHub Copilot */}
          {/* Desktop/Tablet: Bootstrap Tabs with navigation (>= 768px) */}
          <div ref={tabsContainerRef} className={styles.configurationTabs}>
            <Tabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k)}
              className="mb-3"
              id="configuration-tabs"
            >
            <Tab eventKey="delivery-streams" title="Delivery Streams">
              <DeliveryStreamsList user={user} />
            </Tab>
            <Tab eventKey="leadership-search" title="Leadership Search">
              <LeadershipSearch user={user} />
            </Tab>
            {/* US1794882: Brand Awareness tab - only visible to configuration/deity users */}
            {/* Generated by GitHub Copilot */}
            {hasConfigurationPrivilege && (
              <Tab eventKey="brands" title="Brand Awareness">
                <BrandManager user={user} />
              </Tab>
            )}
            {/* US1945335 / US2028928: Global Toggles tab — deity kill switches
                and/or the Modern UI soft-launch toggle (launch admins). */}
            {canViewGlobalToggles && (
              <Tab eventKey="global-toggles" title="Global Toggles">
                <GlobalTogglesList
                  userPrivileges={user?.privileges}
                  isModernUILaunchAdmin={isModernUILaunchAdmin}
                />
              </Tab>
            )}
            </Tabs>
          </div>

          {/* Generated by GitHub Copilot */}
          {/* Mobile: Tab content without navigation (< 768px) */}
          <div className={styles.mobileTabContent}>
            {activeTab === "delivery-streams" && (
              <DeliveryStreamsList user={user} />
            )}
            {activeTab === "leadership-search" && (
              <LeadershipSearch user={user} />
            )}
            {hasConfigurationPrivilege && activeTab === "brands" && (
              <BrandManager user={user} />
            )}
            {/* US1945335 / US2028928: Global Toggles tab — deity and/or launch admin */}
            {canViewGlobalToggles && activeTab === "global-toggles" && (
              <GlobalTogglesList
                userPrivileges={user?.privileges}
                isModernUILaunchAdmin={isModernUILaunchAdmin}
              />
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default Configuration;
